import { filter, shareReplay, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Injectable, NgZone } from '@angular/core';

import { monitorLogger } from 'src/app/project/monitor/monitor-logger';
import { InfoDeviceService, LocalStorageService } from '@project/services';
import { ApiService } from '@core/http';
import { runInZoneOperator, runOutsideZoneOperator } from '@core/helpers';

import { WebSocketClient, WebsocketAvailableProvider, WebsocketChatMessage, WebsocketStatus } from '../definitions';
import { WebsocketClientFactory } from '../factory';
import { ChatUploadHelperService } from '.';

/**
 * Angular boundary for the websocket providers: socket.io/engine.io keep permanent timers (ping/reconnection), and
 * creating them inside the Angular zone keeps ApplicationRef.isStable from ever emitting true, which blocks the
 * post-login guard. Clients run outside the zone and their streams are re-entered here, so providers stay framework-free.
 */
@Injectable({
  providedIn: 'root',
})
export class WebsocketLifecycleService {
  private _currentClient$ = new BehaviorSubject<WebSocketClient | null>(null);
  private _provider$ = new BehaviorSubject<WebsocketAvailableProvider>(null);
  private _preventProviderUpdate$ = new BehaviorSubject<boolean>(false);

  public readonly provider$ = this._provider$.asObservable();

  constructor(
    private localStorageService: LocalStorageService,
    private apiService: ApiService,
    private chatUploaderHelperService: ChatUploadHelperService,
    private infoDeviceService: InfoDeviceService,
    private ngZone: NgZone,
  ) {}

  public readonly messages$: Observable<string> = this._currentClient$.pipe(
    filter((client) => !!client),
    switchMap((client) => client.messages$),
    runInZoneOperator(this.ngZone),
    shareReplay(1),
  );

  public readonly status$: Observable<WebsocketStatus> = this._currentClient$.pipe(
    filter((client) => !!client),
    switchMap((client) => client.status$),
    runInZoneOperator(this.ngZone),
    shareReplay(1),
  );

  public readonly isOnline$: Observable<boolean> = this._currentClient$.pipe(
    filter((client) => !!client),
    switchMap((client) => client.isOnline$),
    runInZoneOperator(this.ngZone),
  );

  public readonly isDown$: Observable<boolean> = this._currentClient$.pipe(
    filter((client) => !!client),
    switchMap((client) => client.isDown$),
    runInZoneOperator(this.ngZone),
  );

  public connect(): Observable<void> {
    const currentClient = this._currentClient$.value;
    if (!currentClient) return of(null);
    return currentClient.connect().pipe(runOutsideZoneOperator(this.ngZone), runInZoneOperator(this.ngZone));
  }

  public setSocketProviderUpdateEnabled(canUpdate: boolean): void {
    this._preventProviderUpdate$.next(!canUpdate);
  }

  public send(message: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    this._currentClient$.value.send(message);
  }

  public sendChatMessage(chatId: string, message: any): Observable<void> {
    return this._currentClient$.value.sendChatMessage(chatId, message);
  }
  public sendChatAttachment(
    chatId: string,
    file: File,
    options?: { [key: string]: any; requisitionId?: string },
  ): Observable<void> {
    return this._currentClient$.value.sendChatAttachment(chatId, file, options);
  }

  public sendServiceMessage(chatId: string, serviceMessage: string, ...additionalParams: string[]): Observable<void> {
    return this._currentClient$.value.sendServiceMessage(chatId, serviceMessage, ...additionalParams);
  }

  public getChatMessagesById(
    chatId: string,
    count: number,
    from: Date = new Date(),
  ): Observable<WebsocketChatMessage[]> {
    return this._currentClient$.value.getChatMessagesById(chatId, count, from);
  }

  public disconnect(): void {
    const currentClient = this._currentClient$.value;
    this.ngZone.runOutsideAngular(() => currentClient?.disconnect());
  }

  public reconnect(): void {
    const currentClient = this._currentClient$.value;
    if (!currentClient) return;

    this.ngZone.runOutsideAngular(() => {
      currentClient.disconnect();
      currentClient.connect().subscribe();
    });
  }

  public destroy(): void {
    const currentClient = this._currentClient$.value;
    this.ngZone.runOutsideAngular(() => currentClient?.destroy());
    this._currentClient$.next(null);
    this._provider$.next(null);
    this._preventProviderUpdate$.next(false);
  }

  public setProvider(provider: WebsocketAvailableProvider): void {
    // If provider updates are currently prevented, do not allow changing the adapter
    if (this._preventProviderUpdate$.value) return;

    // If the requested adapter is the same as the current one and a client already exists, do nothing
    if (this._provider$.value === provider && this._currentClient$.value) return;

    monitorLogger.app.console('LOG', `[WebsocketLifecycle] Switching to: ${provider}`);

    this.destroyCurrentClient();
    this.instantiateNewClient(provider);

    this._provider$.next(provider);
  }

  private destroyCurrentClient(): void {
    const currentClient = this._currentClient$.value;
    if (!currentClient) return;

    try {
      this.ngZone.runOutsideAngular(() => currentClient.destroy());
    } catch (e) {
      monitorLogger.socket.trackError('[WebsocketLifecycle] Error destroying current client');
    }
  }

  private instantiateNewClient(provider: WebsocketAvailableProvider): void {
    const newClient = this.getClientByProvider(provider);
    this._currentClient$.next(newClient);
  }

  private getClientByProvider(provider: WebsocketAvailableProvider): WebSocketClient {
    const factory = new WebsocketClientFactory(
      this.localStorageService,
      this.apiService,
      this.chatUploaderHelperService,
      this.infoDeviceService,
    );

    return factory.getProvider(provider);
  }
}
